import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import {
  ArrowRight,
  Calendar,
  CircleDot,
  Filter,
  Flag,
  Globe,
  Layers,
  MessageSquareText,
  Package,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
  type LucideIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { useFormatting } from '@/admin/hooks/use-formatting'
import {
  useAvailabilityRules,
  useDeleteAvailabilityRule,
  type AvailabilityRule,
} from '@/admin/hooks/use-availability-rules'
import type { Severity } from '@/admin/hooks/use-validation-rules'
import {
  flattenProductCategories,
  useProductCategories,
  useProductTemplates,
} from '@/admin/hooks/use-products'
import { useAvailableRefs } from '@/admin/components/condition-builder/use-available-refs'
import {
  OP_LABELS,
  VALUELESS_OPS,
  predicateToBuilder,
  type ClauseNode,
  type PredicateNode,
} from '@/admin/components/condition-builder/types'
import { SearchInput } from '@/shared/components/ui/search-input'
import { Button } from '@/shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { FilteringRuleDialog } from '@/admin/pages/manage/product-filtering/filtering-rule-dialog'
import { IconButton } from '@/admin/pages/manage/warranty-class-detail/shared'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { PaginationControls } from '@/admin/components/paginated-table'
import { cn } from '@/shared/lib/utils'
import { toastApiError } from '@/shared/lib/api-error'

const DEFAULT_PAGE_SIZE = 20

type StatusFilter = 'all' | 'active' | 'inactive'
type SeverityFilter = 'all' | Severity

const SEVERITY_LABELS: Record<Severity, string> = {
  BLOCK: 'Block',
  WARN: 'Warn',
  INFO: 'Info',
}

const SEVERITY_TEXT_TONE: Record<Severity, string> = {
  BLOCK: 'text-[var(--status-error)]',
  WARN: 'text-[var(--status-warn)]',
  INFO: 'text-[var(--status-info)]',
}

function stringifyConditionValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map((entry) => stringifyConditionValue(entry)).join(', ')
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (value === null || value === undefined || value === '') return 'blank'
  return String(value)
}

interface ClauseSummary {
  ref: string
  label: string
  op: string
  opLabel: string
  value?: string
  hasValue: boolean
}

function summariseClauses(
  conditions: PredicateNode | null,
  byRef: (ref: string) => { label: string } | undefined,
): ClauseSummary[] | null {
  if (!conditions) return null
  const builderState = predicateToBuilder(conditions)
  const clauses = builderState.clauses.filter((clause) => clause.ref && clause.op)
  if (clauses.length === 0) return null
  return clauses.map((clause: ClauseNode) => {
    const label = byRef(clause.ref)?.label ?? clause.ref
    const opLabel = OP_LABELS[clause.op] ?? clause.op
    const hasValue = !VALUELESS_OPS.has(clause.op)
    return {
      ref: clause.ref,
      label,
      op: clause.op,
      opLabel,
      hasValue,
      value: hasValue ? stringifyConditionValue(clause.value) : undefined,
    }
  })
}

interface TargetGroup {
  label: string
  values: string[]
  tone?: 'green' | 'neutral'
  icon: LucideIcon
}

function collectTargetGroups(
  rule: AvailabilityRule,
): TargetGroup[] {
  const groups: TargetGroup[] = []
  const filters = rule.query_filters ?? {}
  const meta = rule.query_filter_meta ?? {}

  if (filters.deny_all) {
    groups.push({
      label: 'Outcome',
      values: ['No products'],
      tone: 'neutral',
      icon: Filter,
    })
  }
  if ((filters.warranty_types ?? []).length > 0) {
    groups.push({
      label: 'Status',
      values:
        meta.warranty_types?.map((entry) => entry.label) ?? (filters.warranty_types ?? []),
      tone: 'neutral',
      icon: CircleDot,
    })
  }
  if ((filters.category_ids ?? []).length > 0) {
    groups.push({
      label: 'Categories',
      values:
        meta.category_ids?.map((entry) => entry.label) ?? (filters.category_ids ?? []),
      tone: 'green',
      icon: Layers,
    })
  }
  if ((filters.template_ids ?? []).length > 0) {
    groups.push({
      label: 'Templates',
      values:
        meta.template_ids?.map((entry) => entry.label) ?? (filters.template_ids ?? []),
      tone: 'green',
      icon: Package,
    })
  }
  if ((filters.geo_zone_ids ?? []).length > 0) {
    groups.push({
      label: 'Geozones',
      values:
        meta.geo_zone_ids?.map((entry) => entry.label) ?? (filters.geo_zone_ids ?? []),
      tone: 'green',
      icon: Globe,
    })
  }
  if ((filters.attribute_filters ?? []).length > 0) {
    groups.push({
      label: 'Attributes',
      values: (filters.attribute_filters ?? []).map(
        (filter) => `${filter.code_name} = ${String(filter.value)}`,
      ),
      tone: 'neutral',
      icon: SlidersHorizontal,
    })
  }
  return groups
}

export function ProductFilteringPage() {
  const { formatDate } = useFormatting()
  const [searchParams, setSearchParams] = useSearchParams()
  const statusFilter = (searchParams.get('status') as StatusFilter | null) ?? 'all'
  const severityFilter =
    (searchParams.get('severity') as SeverityFilter | null) ?? 'all'
  const search = searchParams.get('q') ?? ''
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1)
  const pageSize = Math.max(
    1,
    Number(searchParams.get('pageSize') ?? String(DEFAULT_PAGE_SIZE)) ||
      DEFAULT_PAGE_SIZE,
  )

  const params = useMemo(() => {
    const next: {
      scope: 'product'
      is_active?: 'true' | 'false'
      severity?: Severity
      q?: string
    } = {
      scope: 'product',
    }
    if (statusFilter === 'active') next.is_active = 'true'
    if (statusFilter === 'inactive') next.is_active = 'false'
    if (severityFilter !== 'all') next.severity = severityFilter
    if (search.trim()) next.q = search.trim()
    return next
  }, [search, severityFilter, statusFilter])

  const { data: rules, isLoading } = useAvailabilityRules(params)
  const deleteRule = useDeleteAvailabilityRule()
  const { data: productTemplates = [] } = useProductTemplates()
  const { data: productCategoriesTree = [] } = useProductCategories()
  // Categories come back as a tree of root nodes with nested children. Flatten
  // so chip rendering and UUID -> name lookups work for child categories too.
  const productCategories = useMemo(
    () => flattenProductCategories(productCategoriesTree),
    [productCategoriesTree],
  )
  const { byRef } = useAvailableRefs()
  const [showRuleDialog, setShowRuleDialog] = useState(false)
  const [editingRule, setEditingRule] = useState<AvailabilityRule | null>(null)
  const [deletingRule, setDeletingRule] = useState<AvailabilityRule | null>(null)
  const availableAttributes = useMemo(() => {
    const seen = new Set<string>()
    return productTemplates.flatMap((template) =>
      (template.attributes ?? []).filter((attribute) => {
        if (seen.has(attribute.code_name)) return false
        seen.add(attribute.code_name)
        return true
      }),
    )
  }, [productTemplates])

  const hasActiveFilters =
    statusFilter !== 'all' || severityFilter !== 'all' || search.trim().length > 0

  function updateFilters(next: Record<string, string | null>) {
    const merged = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(next)) {
      if (!value || value === 'all') merged.delete(key)
      else merged.set(key, value)
    }
    // Any filter change snaps the user back to page 1 so they don't land on
    // an empty tail page after the result count shrinks.
    merged.delete('page')
    setSearchParams(merged, { replace: true })
  }

  function setPage(nextPage: number) {
    const merged = new URLSearchParams(searchParams)
    if (nextPage <= 1) merged.delete('page')
    else merged.set('page', String(nextPage))
    setSearchParams(merged, { replace: true })
  }

  function setPageSize(nextSize: number) {
    const merged = new URLSearchParams(searchParams)
    if (nextSize === DEFAULT_PAGE_SIZE) merged.delete('pageSize')
    else merged.set('pageSize', String(nextSize))
    merged.delete('page')
    setSearchParams(merged, { replace: true })
  }

  // Per-product (scope_id != null) rules - e.g. those auto-migrated from
  // ``ProductAvailability`` - are managed on the product detail page and
  // would only confuse this global filtering view, so we hide them here.
  const globalRules = useMemo(
    () => (rules ?? []).filter((rule) => !rule.scope_id),
    [rules],
  )
  const totalCount = globalRules.length
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))
  const safePage = Math.min(page, totalPages)
  const pagedRules = useMemo(() => {
    const start = (safePage - 1) * pageSize
    return globalRules.slice(start, start + pageSize)
  }, [globalRules, safePage, pageSize])

  function openCreate() {
    setEditingRule(null)
    setShowRuleDialog(true)
  }

  async function confirmDeleteRule() {
    if (!deletingRule) return
    try {
      await deleteRule.mutateAsync(deletingRule.uuid)
      toast.success('Rule deleted')
      setDeletingRule(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete rule')
    }
  }

  return (
    <>
      {/* Section header */}
      <div className="mt-6 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-6">
          <div className="max-w-[560px]">
            <p className="clara-section-label flex items-center gap-2">
              <Filter className="h-3 w-3 text-[var(--ink-3)]" strokeWidth={1.5} />
              Product rules
            </p>
            <p className="clara-small mt-2">
              Match registration data, then narrow which products appear in
              the next step of the registration flow.
            </p>
          </div>
          <Button
            type="button"
            onClick={openCreate}
            className="h-auto shrink-0 cursor-pointer rounded-[4px] bg-[var(--green)] px-3.5 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-colors duration-150 hover:bg-[var(--green-hover)]"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.75} />
            New rule
          </Button>
        </div>

        {/* Toolbar */}
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_160px_160px]">
          <SearchInput
            value={search}
            onChange={(value) => updateFilters({ q: value || null })}
            placeholder="Search rules..."
            iconSize={12}
            className="h-9 text-[12px]"
          />
          <Select
            value={statusFilter}
            onValueChange={(value: string) => updateFilters({ status: value })}
          >
            <SelectTrigger className="h-9 text-[12px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="inactive">Inactive only</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={severityFilter}
            onValueChange={(value: string) => updateFilters({ severity: value })}
          >
            <SelectTrigger className="h-9 text-[12px]">
              <SelectValue placeholder="Effect" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All effects</SelectItem>
              <SelectItem value="BLOCK">Block</SelectItem>
              <SelectItem value="WARN">Warn</SelectItem>
              <SelectItem value="INFO">Info</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      <div className="mt-4">
        {isLoading ? (
          <RulesSkeleton />
        ) : !globalRules.length ? (
          <EmptyState
            icon={Filter}
            title={
              hasActiveFilters ? 'No rules match these filters' : 'No rules yet'
            }
            description={
              hasActiveFilters
                ? 'Try clearing the filters above to see every rule.'
                : 'Add a rule to narrow which products appear during registration.'
            }
            action={
              hasActiveFilters
                ? undefined
                : { label: 'New rule', onClick: openCreate }
            }
          />
        ) : (
          <div className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
            {pagedRules.map((rule, idx) => {
              const clauses = summariseClauses(rule.conditions, byRef)
              const targetGroups = collectTargetGroups(rule)
              const severityLabel = SEVERITY_LABELS[rule.severity] ?? rule.severity
              const severityTextClass =
                SEVERITY_TEXT_TONE[rule.severity] ?? 'text-[var(--ink-3)]'
              return (
                <article
                  key={rule.uuid}
                  className={cn(
                    'group relative flex items-start gap-3 px-4 py-2.5 transition-colors duration-150 hover:bg-[var(--bg-subtle)]',
                    idx < pagedRules.length - 1 &&
                      'border-b-[0.5px] border-b-[var(--rule)]',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    {/* Title row: name · status · severity  --  meta on the right */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <h3 className="text-[12.5px] font-medium leading-5 text-[var(--ink)]">
                        {rule.name || 'Unnamed filter'}
                      </h3>
                      <MetaDot />
                      <span
                        className={cn(
                          'text-[10.5px] font-medium uppercase leading-5 tracking-[0.08em]',
                          rule.is_active
                            ? 'text-[var(--status-ok)]'
                            : 'text-[var(--ink-3)]',
                        )}
                      >
                        {rule.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <MetaDot />
                      <span
                        className={cn(
                          'text-[10.5px] font-medium uppercase leading-5 tracking-[0.08em]',
                          severityTextClass,
                        )}
                      >
                        {severityLabel}
                      </span>
                      <div className="ml-auto flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-[var(--ink-3)]">
                        <span className="inline-flex items-center gap-1">
                          <Flag className="h-2.5 w-2.5" strokeWidth={1.75} />
                          Priority {rule.priority}
                        </span>
                        <MetaDot />
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-2.5 w-2.5" strokeWidth={1.75} />
                          {rule.effective_from || rule.effective_until ? (
                            <>
                              {rule.effective_from
                                ? formatDate(rule.effective_from)
                                : 'Anytime'}
                              {' - '}
                              {rule.effective_until
                                ? formatDate(rule.effective_until)
                                : 'No end date'}
                            </>
                          ) : (
                            'Always active'
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Conditions / targets - When + Then on one line, wraps on small screens */}
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1">
                      <ClauseRow icon={Search} label="When" hasContent={!!clauses}>
                        {clauses ? (
                          <div className="flex flex-wrap items-center gap-1">
                            {clauses.map((clause, clauseIdx) => (
                              <ClauseChip
                                key={`${clause.ref}-${clauseIdx}`}
                                clause={clause}
                              />
                            ))}
                          </div>
                        ) : (
                          <span className="text-[11px] text-[var(--ink-3)]">
                            Always evaluate
                          </span>
                        )}
                      </ClauseRow>

                      <ClauseRow
                        icon={ArrowRight}
                        label="Then"
                        hasContent={targetGroups.length > 0}
                      >
                        {targetGroups.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                            {targetGroups.map((group) => (
                              <TargetGroupChip key={group.label} group={group} />
                            ))}
                          </div>
                        ) : (
                          <span className="text-[11px] text-[var(--ink-3)]">
                            No query targets configured
                          </span>
                        )}
                      </ClauseRow>
                    </div>

                    {rule.description && (
                      <p className="mt-1 flex items-start gap-1.5 text-[11px] leading-snug text-[var(--ink-3)]">
                        <MessageSquareText
                          className="mt-[2px] h-2.5 w-2.5 shrink-0"
                          strokeWidth={1.75}
                        />
                        <span>{rule.description}</span>
                      </p>
                    )}
                  </div>

                  {/* Actions - visible at idle, brighter on hover/focus */}
                  <div className="flex shrink-0 items-center gap-0.5 opacity-40 transition-opacity duration-150 group-hover:opacity-100 focus-within:opacity-100">
                    <IconButton
                      onClick={() => {
                        setEditingRule(rule)
                        setShowRuleDialog(true)
                      }}
                      title="Edit rule"
                    >
                      <Pencil className="h-3 w-3" />
                    </IconButton>
                    <IconButton
                      onClick={() => setDeletingRule(rule)}
                      title="Delete rule"
                    >
                      <Trash2 className="h-3 w-3" />
                    </IconButton>
                  </div>
                </article>
              )
            })}
          </div>
        )}

        {totalCount > 0 && (
          <PaginationControls
            page={safePage}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        )}
      </div>

      <FilteringRuleDialog
        attributes={availableAttributes}
        productTemplates={productTemplates}
        productCategories={productCategories}
        open={showRuleDialog}
        onOpenChange={(open) => {
          setShowRuleDialog(open)
          if (!open) setEditingRule(null)
        }}
        editing={editingRule}
      />

      <ConfirmDeleteDialog
        open={!!deletingRule}
        onOpenChange={(open) => {
          if (!open) setDeletingRule(null)
        }}
        title="Delete rule?"
        description={
          <>
            Are you sure you want to delete{' '}
            <strong>{deletingRule?.name || 'this rule'}</strong>? This
            action cannot be undone.
          </>
        }
        onConfirm={confirmDeleteRule}
        isPending={deleteRule.isPending}
      />
    </>
  )
}

function ClauseRow({
  icon: Icon,
  label,
  hasContent,
  children,
}: {
  icon: LucideIcon
  label: string
  hasContent: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span
        className={cn(
          'inline-flex shrink-0 items-center gap-1 text-[9.5px] font-normal uppercase leading-4 tracking-[0.12em]',
          hasContent ? 'text-[var(--ink-3)]' : 'text-[var(--ink-3)]/60',
        )}
      >
        <Icon className="h-2.5 w-2.5" strokeWidth={1.75} />
        {label}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

function ClauseChip({ clause }: { clause: ClauseSummary }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-[3px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-1.5 py-px text-[11px] leading-4">
      <span className="text-[var(--ink-2)]">{clause.label}</span>
      <span className="text-[var(--ink-3)]/80">{clause.opLabel}</span>
      {clause.hasValue && (
        <span className="font-medium text-[var(--ink)]">{clause.value}</span>
      )}
    </span>
  )
}

function TargetGroupChip({ group }: { group: TargetGroup }) {
  const valueClasses =
    group.tone === 'green'
      ? 'bg-[var(--green-bg)] text-[var(--green)]'
      : 'bg-[var(--bg-subtle)] text-[var(--ink-2)]'
  const Icon = group.icon
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="inline-flex items-center gap-1 text-[9.5px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
        <Icon className="h-2.5 w-2.5" strokeWidth={1.75} />
        {group.label}
      </span>
      {group.values.map((value) => (
        <span
          key={value}
          className={cn(
            'inline-flex items-center rounded-[3px] px-1.5 py-px text-[11px] leading-4',
            valueClasses,
          )}
        >
          {value}
        </span>
      ))}
    </div>
  )
}

function MetaDot() {
  return <span className="select-none opacity-40">·</span>
}

function RulesSkeleton() {
  return (
    <div className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
      {[0, 1, 2].map((idx) => (
        <div
          key={idx}
          className={cn(
            'flex items-start gap-3 px-4 py-2.5',
            idx < 2 && 'border-b-[0.5px] border-b-[var(--rule)]',
          )}
        >
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="h-3 w-48 animate-pulse rounded-[3px] bg-[var(--skeleton-base)]" />
              <div className="h-3.5 w-12 animate-pulse rounded-[4px] bg-[var(--skeleton-base)]" />
              <div className="h-3.5 w-12 animate-pulse rounded-[4px] bg-[var(--skeleton-base)]" />
            </div>
            <div className="h-3 w-3/4 animate-pulse rounded-[3px] bg-[var(--skeleton-base)]" />
            <div className="h-3 w-1/2 animate-pulse rounded-[3px] bg-[var(--skeleton-base)]" />
          </div>
        </div>
      ))}
    </div>
  )
}

export default ProductFilteringPage
